import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { z } from 'zod'

import { ModalLayout } from '@/components/ui/modal-layout'
import { useAppForm } from '@/lib/forms/hook'

export interface RenameSessionModalProps {
  showModal: boolean
  setShowModal: (showModal: boolean) => void
  initialName: string
  onRename: (name: string) => Promise<void> | void
}

const renameSessionSchema = z.object({
  renameSessionName: z.string().min(1, 'กรุณาระบุชื่อเซสชัน'),
})

export function RenameSessionModal({
  showModal,
  setShowModal,
  initialName,
  onRename,
}: RenameSessionModalProps) {
  const form = useAppForm({
    defaultValues: {
      renameSessionName: initialName || '',
    },
    validators: {
      onChange: renameSessionSchema,
    },
    onSubmit: async ({ value }) => {
      await onRename(value.renameSessionName)
      setShowModal(false)
    },
  })

  useEffect(() => {
    if (showModal) {
      form.reset({
        renameSessionName: initialName || '',
      })
    }
  }, [showModal, initialName, form])

  return (
    <form.Subscribe selector={(state) => state.values.renameSessionName}>
      {(renameSessionName) => (
        <ModalLayout
          open={showModal}
          onOpenChange={setShowModal}
          title="แก้ไขชื่อเซสชัน"
          description="แก้ไขชื่อของเซสชันนี้"
          confirmText="บันทึก"
          isConfirmDisabled={renameSessionName.trim() === ''}
          onConfirm={() => {
            form.handleSubmit()
          }}
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
        >
          <form.AppField name="renameSessionName">
            {(field) => (
              <field.TextField
                label="ชื่อเซสชัน"
                placeholder="ชื่อเซสชัน"
                className="h-12 rounded-full border-rose-200 bg-rose-50/10 px-5 font-bold focus-visible:border-rose-300 focus-visible:ring-2 focus-visible:ring-rose-500/20 focus-visible:ring-offset-0"
                autoFocus
              />
            )}
          </form.AppField>
        </ModalLayout>
      )}
    </form.Subscribe>
  )
}

export function useRenameSessionModal() {
  const [showRenameSessionModal, setShowRenameSessionModal] = useState(false)

  const RenameSessionModalCallback = useCallback(
    ({
      initialName,
      onRename,
    }: {
      initialName: string
      onRename: (name: string) => Promise<void> | void
    }) => {
      return (
        <RenameSessionModal
          showModal={showRenameSessionModal}
          setShowModal={setShowRenameSessionModal}
          initialName={initialName}
          onRename={onRename}
        />
      )
    },
    [showRenameSessionModal],
  )

  return useMemo(
    () => ({
      setShowRenameSessionModal,
      RenameSessionModal: RenameSessionModalCallback,
    }),
    [setShowRenameSessionModal, RenameSessionModalCallback],
  )
}
